import Analytics from '@/utils/analytics';
import ForbiddenIcon from '@assets/icons/forbidden.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import SearchIcon from '@assets/icons/search.svg';
import SwapIcon from '@assets/icons/swap.svg';
import { createStyles } from '@/contexts/theme-context';
import { getRememberSelectionFromItem } from './utils';
import { InboundListItem } from './types';
import { Tooltip } from '@usepepper/ui/atoms/tooltip';
import { TouchableOpacity, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';

interface Props {
  disabled?: boolean;
  inboundListItem: InboundListItem;
  navigateToSearch: (item: InboundListItem) => void;
}

const ICON_SIZE = 16;
const ICON_BUTTON_SIZE = 32;
const STATUS_ICON_SIZE = 8;
const STATUS_ICON_OFFSET = -4;

export const InboundDetailListItemAction: React.FC<Props> = ({
  disabled,
  inboundListItem,
  navigateToSearch,
}) => {
  const { isViewOnly } = useOrderInbounds();
  const metadata = inboundListItem.metadata!;
  const itemHasMatch = Boolean(metadata.listOption);
  const rememberSelection = getRememberSelectionFromItem(inboundListItem);

  const [styles, theme] = useStyles();

  const onPress = () => {
    if (itemHasMatch) {
      Analytics.track('order_inbound_detail.swap_item', {
        searchTerm: inboundListItem.title,
      });
    } else {
      Analytics.track('order_inbound_detail.search_item', {
        searchTerm: inboundListItem.title,
      });
    }

    navigateToSearch(inboundListItem);
  };

  return !isViewOnly && !disabled ? (
    <TouchableOpacity
      onPress={onPress}
      style={styles.icon}
      testID={
        itemHasMatch
          ? 'order-inbound-swap-button'
          : 'order-inbound-search-button'
      }
    >
      {itemHasMatch ? (
        <>
          <SwapIcon
            height={ICON_SIZE}
            stroke={theme.sk.color.icon.primary}
            width={ICON_SIZE}
          />
          {!rememberSelection ? (
            <View style={styles.forbiddenIconContainer}>
              <Tooltip
                containerStyle={styles.tooltipContainer}
                placement="bottom"
                tabIndex={-1}
                trigger={
                  <ForbiddenIcon
                    height={STATUS_ICON_SIZE}
                    stroke={theme.sk.color.icon.primary}
                    testID="order-inbound-swap-button-remember-selection-disabled-icon"
                    width={STATUS_ICON_SIZE}
                  />
                }
              >
                <Typography
                  color={theme.sk.color.text.ondark}
                  variant="paragraph-md"
                >
                  {i18n.t('remember_selection.icon_tooltip')}
                </Typography>
              </Tooltip>
            </View>
          ) : null}
        </>
      ) : (
        <>
          <SearchIcon
            fill={theme.sk.color.icon.primary}
            height={ICON_SIZE}
            width={ICON_SIZE}
          />
        </>
      )}
    </TouchableOpacity>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  forbiddenIconContainer: {
    backgroundColor: theme.sk.color.bg.blue,
    borderRadius: 16,
    bottom: STATUS_ICON_OFFSET,
    padding: 4,
    position: 'absolute',
    right: STATUS_ICON_OFFSET,
  },
  icon: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderRadius: ICON_BUTTON_SIZE,
    height: ICON_BUTTON_SIZE,
    justifyContent: 'center',
    width: ICON_BUTTON_SIZE,
  },
  tooltipContainer: {
    backgroundColor: theme.sk.color.bg.toast,
  },
}));
